import { Injectable, EventEmitter } from '@angular/core';
import { ApiService } from '../../core/services/api.service';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, switchMap, catchError } from 'rxjs/operators';
import { codetype, ActioncodeInterface } from '../entities/bookingParameters';

@Injectable({
  providedIn: 'root'
})
export class PromocodeService {
  private searchText$ = new Subject<string>();
  readonly currentState = new Subject<boolean>();
  public partner: string;

  /**
   * will return true if a new phrase is accepted (after debounce)
   * will return false directly before results are returned by onValidChange
   */
  readonly onSearching: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * is fired everytime a new phrase is accepted as valid (after debounce)
   */
  readonly onValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  readonly newCode: EventEmitter<ActioncodeInterface> = new EventEmitter<ActioncodeInterface>();

  constructor(private apiService: ApiService) {
    this.searchText$
      .pipe(
        debounceTime(700),
        // distinctUntilChanged(),
        switchMap(newPhrase => {
          // TODO: we might need to cache the phrase and emit it back again onValidChange?
          this.onSearching.emit(true);
          return this.searchCode(newPhrase);
        })
      )
      .subscribe({
        next: response => {
          this.onSearching.emit(false);
          this.newCode.emit(response);
          if (response.errorCode) {
            this.onValidChange.emit(false);
            this.currentState.next(false);
          } else {
            this.onValidChange.emit(true);
            this.currentState.next(true);
          }
        },
        error: () => {
          this.onSearching.emit(false);
          this.onValidChange.emit(false);
          this.currentState.next(false);
        }
      });
  }

  /**
   * update the currenct promocode phrase
   * @param phrase promotioncode phrase (like everest)
   */
  public updatePhrase(phrase: string) {
    this.searchText$.next(phrase.toUpperCase());
  }

  public searchCode(code: string): Observable<ActioncodeInterface> {
    return this.apiService.checkPromotionCode(code.toUpperCase(), this.partner).pipe(
      // catches inner error so that outer pipe won't stop propagating new inputs.
      // returnes false because 404 response on this api is always promocode not found.
      catchError(() => {
        if (code.length === 12) {
          // a coupon code?
          return this.apiService.checkCouponCode(code.toUpperCase()).pipe(
            catchError(() => {
              // TODO: err contains array of errors from server response, not found | date invalid | etc
              return of({ type: codetype.coupon, text: code.toUpperCase(), errorCode: 404100 });
            })
          );
        }
        return of({ type: codetype.promo, text: code.toUpperCase(), errorCode: 404100 }); // promocode invalid
      })
    );
  }
}
